import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { AvailableLangs, TranslocoService } from '@ngneat/transloco';
import { FuseNavigationItem, FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { LANGUAGE_STORAGE_KEY } from 'app/core/transloco/transloco.module';

@Component({
    selector: 'languages',
    templateUrl: './languages.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'languages'
})
export class LanguagesComponent implements OnInit, OnDestroy {
    availableLangs: AvailableLangs;
    activeLang: string;

    /**
     * Títulos originais do menu (em inglês, como vêm de mock-api/common/navigation/data.ts),
     * guardados na primeira passagem para servir de fallback quando uma chave de
     * tradução ainda não existe.
     */
    private _originalNavTitles = new Map<string, { title: string; subtitle: string }>();
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _fuseNavigationService: FuseNavigationService,
        private _translocoService: TranslocoService
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Get the available languages from transloco
        this.availableLangs = this._translocoService.getAvailableLangs();

        // Subscribe to language changes
        this._translocoService.langChanges$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((activeLang) => {

                // Get the active lang
                this.activeLang = activeLang;

                // O arquivo do idioma pode ainda não ter sido baixado; só traduzimos
                // a navegação depois que ele estiver em memória.
                this._translocoService.load(activeLang)
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(() => {
                        this._updateNavigation();
                        this._changeDetectorRef.markForCheck();
                    });

                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Set the active lang
     *
     * @param lang
     */
    setActiveLang(lang: string): void {
        // Set the active lang
        this._translocoService.setActiveLang(lang);

        // Lembra a escolha para as próximas sessões
        try {
            localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
        }
        catch {
            // localStorage indisponível — a troca vale só para esta sessão.
        }
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Traduz os títulos do menu lateral para o idioma ativo.
     *
     * As chaves seguem o id do item de navegação: `nav.<id>` para o título e
     * `navSubtitle.<id>` para o subtítulo. Item sem chave correspondente mantém
     * o texto original em inglês.
     *
     * @private
     */
    private _updateNavigation(): void {
        // Get the component -> navigation data -> item
        const navComponent = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation');

        // Return if the navigation component does not exist
        if (!navComponent) {
            return null;
        }

        const navigation = navComponent.navigation;
        if (!navigation) {
            return null;
        }

        this._translateItems(navigation);

        // Refresh the navigation component
        navComponent.refresh();
    }

    /**
     * Percorre a árvore de navegação aplicando as traduções.
     *
     * @param items
     * @private
     */
    private _translateItems(items: FuseNavigationItem[]): void {
        items.forEach((item) => {
            if (item.id) {
                // Guarda o texto original na primeira passagem
                if (!this._originalNavTitles.has(item.id)) {
                    this._originalNavTitles.set(item.id, {
                        title: item.title,
                        subtitle: item.subtitle
                    });
                }

                const original = this._originalNavTitles.get(item.id);

                if (original.title) {
                    item.title = this._translateOr(`nav.${item.id}`, original.title);
                }

                if (original.subtitle) {
                    item.subtitle = this._translateOr(`navSubtitle.${item.id}`, original.subtitle);
                }
            }

            if (item.children?.length) {
                this._translateItems(item.children);
            }
        });
    }

    /**
     * Traduz a chave; devolve o fallback quando ela não existe no idioma ativo.
     * O transloco devolve a própria chave quando não encontra a tradução.
     *
     * @param key
     * @param fallback
     * @private
     */
    private _translateOr(key: string, fallback: string): string {
        const translation = this._translocoService.translate(key);
        return !translation || translation === key ? fallback : translation;
    }
}
